import { AfterViewInit, Component, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ModalHeaderComponent } from 'src/app/shared/modals/components/modal-header/modal-header.component';
import { Modal } from 'src/app/shared/modals/modal';
import { ISessionLecturerAttendanceInputGroupForm, SessionLecturerAttendanceInputGroupComponent } from './session-lecturer-attendance-input-group/session-lecturer-attendance-input-group.component';
import { SessionLecturerType } from 'src/app/shared/enums/session-group/session-lecturer-types.enum';
import { ICallablesSessionsGetForSessionDetailCardModalResult, SessionsService } from 'src/app/shared/services/entities/sessions/sessions.service';
import { Subscription, take } from 'rxjs';
import { ShopItemHelperService } from 'src/app/shared/services/helpers/shop-item-helper.service';
import { SessionFormat } from 'src/app/shared/enums/session-group/session-formats.enum';
import { DividerModule } from 'primeng/divider';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IModalFooterMenuItem, ModalFooterMenuComponent } from 'src/app/shared/components/modal-footer-menu/modal-footer-menu.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SessionAttendanceModalComponent } from 'src/app/shared/modals/session-attendance-modal/session-attendance-modal.component';
import { StoreService } from 'src/app/shared/services/store/store.service';
import { SessionAttendanceState } from 'src/app/shared/models/session-attendance/session-attendance.model';
import { OrganizationStoreService } from 'src/app/shared/services/store/organization-store.service';
import { IOrganization } from 'src/app/shared/models/organization/organization.model';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationService } from 'src/app/shared/services/navigation.service';

export enum SessionDetailCardModalType {
  ADMIN = 'ADMIN',
  LECTURER = 'LECTURER',
}

export enum SessionDetailCardInfoCardType {
  DATETIME = 'DATETIME',
  TIME = 'TIME',
  LOCATION = 'LOCATION',
  SEND_MESSAGE = 'SEND_MESSAGE',
  PARTICIPANTS = 'PARTICIPANTS',
  MATERIALS = 'MATERIALS',
  PARTICIPANT_ATTENDANCE = 'PARTICIPANT_ATTENDANCE',
  CHECKIN = 'CHECKIN',
}

@Component({
  selector: 'app-session-detail-card-modal',
  standalone: true,
  imports: [
    TranslateModule, NgTemplateOutlet,
    DividerModule,
    ModalHeaderComponent, SessionLecturerAttendanceInputGroupComponent, ModalFooterMenuComponent,
    NgbTooltipModule,
  ],
  templateUrl: './session-detail-card-modal.component.html',
  styleUrl: './session-detail-card-modal.component.scss'
})
export class SessionDetailCardModalComponent extends Modal implements OnInit, AfterViewInit, OnDestroy {
  SessionDetailCardModalType = SessionDetailCardModalType;
  SessionLecturerType = SessionLecturerType;
  SessionFormat = SessionFormat;
  SessionDetailCardInfoCardType = SessionDetailCardInfoCardType;

  @Input({required: true}) sessionUuid: string | undefined;
  @Input({required: true}) type: SessionDetailCardModalType = SessionDetailCardModalType.ADMIN;

  private _sessionLecturerAttendanceInputGroup: SessionLecturerAttendanceInputGroupComponent | undefined;
  get sessionLecturerAttendanceInputGroup(): SessionLecturerAttendanceInputGroupComponent | undefined { return this._sessionLecturerAttendanceInputGroup; }
  @ViewChild('sessionLecturerAttendanceInputGroup') set sessionLecturerAttendanceInputGroup(val: SessionLecturerAttendanceInputGroupComponent) {
    this._sessionLecturerAttendanceInputGroup = val;
    this.handleSessionLecturerAttendanceInputGroup();
  };

  sessionData: ICallablesSessionsGetForSessionDetailCardModalResult | null = null;
  defaultParticipantCountValue: string = '-';
  participantCountValue: string = this.defaultParticipantCountValue;
  participantAttendanceStatus: 'some-unfilled' | 'all-filled' | null = null;

  infoCardBlock1: SessionDetailCardInfoCardType[] = [];
  infoCardBlock2: SessionDetailCardInfoCardType[] = [];

  footerMenuItems: IModalFooterMenuItem[] = [];

  @HostBinding('class.loading-box') fetching: boolean = false;

  subs: Subscription[] = [];

  constructor(
    private sessionsService: SessionsService,
    public siHelper: ShopItemHelperService,
    private modalService: ModalService,
    private store: StoreService,
    private orgStore: OrganizationStoreService,
    private navService: NavigationService
  ) {
    super();

    this.subs.push(
      this.orgStore.state.org$.subscribe(org => {
        this.initInfoCards(org);
      })
    )
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.fetchSessionData();

  }

  ngAfterViewInit(): void {

  }


  private fetchSessionData() {
    if (!this.sessionUuid) return;

    this.fetching = true;
    this.sessionsService.getForSessionDetailCardModal({
      sessionUuid: this.sessionUuid
    }).pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        this.sessionData = res;
        this.fetching = false;
        this.initParticipantCount();
      },
      error: (err) => {
        this.fetching = false;
        this.sessionData = null;
      }
    })
  }

  private initParticipantCount() {
    if (!this.sessionData) {
      this.participantCountValue = this.defaultParticipantCountValue;
      return;
    }
    

    const notFilledCnt = this.sessionData.sessionAttendances.filter(x => x.noAttendanceYet === true || x.state === null).length;
    const presentCnt = this.sessionData.sessionAttendances.filter(x => x.noAttendanceYet === false).filter(x => 'state' in x && x.state === SessionAttendanceState.PRESENT).length;
    // const absentCnt = this.sessionData.sessionAttendances.filter(x => x.noAttendanceYet === false).filter(x => 'state' in x && x.state === SessionAttendanceState.ABSENT).length;
    const totalCnt = this.sessionData.sessionAttendances.length;

    if (notFilledCnt) this.participantAttendanceStatus = 'some-unfilled';
    if (totalCnt && this.sessionData.sessionAttendances.every((att) => !att.noAttendanceYet && att.state !== null)) this.participantAttendanceStatus = 'all-filled';

    let firstPartCnt = 0;

    const now = new Date();

    if (now > new Date(this.sessionData.endAt).localDateToUTC()) {
      firstPartCnt = presentCnt;
    } else {
      firstPartCnt = notFilledCnt + presentCnt;
    }

    if (firstPartCnt === totalCnt) {
      this.participantCountValue = `${totalCnt}`;
    } else {
      this.participantCountValue = `${firstPartCnt} / ${totalCnt}`;
    }

  }

  onMeetingUrl(meetingUrl: string) {
    window.open(meetingUrl, '_blank');
  }

  onLocationMapUrl(mapUrl: string | null) {
    if (mapUrl) {
      window.open(mapUrl, '_blank');
    }
  }



  private initFooterMenuItems(c: SessionLecturerAttendanceInputGroupComponent | undefined) {
    const FG = c?.FG;
    const items: IModalFooterMenuItem[] = [];

    if (FG?.dirty) {
      items.push({
        icon: 'pi pi-times',
        translationText: 'shared.footer-menu.item.close-no-save.label',
        action: () => {
          this.close();
        },
      });
    } else {
      items.push({
        icon: 'pi pi-chevron-left',
        translationText: 'shared.footer-menu.item.close.label',
        action: () => {
          this.close();
        },
      });
    }


    if (FG?.dirty) {
      items.push({
        icon: 'pi pi-save',
        translationText: 'shared.footer-menu.item.save-close.label',
        action: () => {
          const res = c?.onSubmit();
          res?.pipe(take(1)).subscribe((r) => {
            if (r) {
              this.store.actions.sessionLecturerAttendance_sessionLecturerAttendanceChanged$.next({ sessionId: this.sessionData!.id });
              this.close();
            }
          })
        },
        isLoading: () => c?.sending ?? false,
      });
    }

    this.footerMenuItems = items;
  }

  private handleSessionLecturerAttendanceInputGroup() {
    this.initFooterMenuItems(this.sessionLecturerAttendanceInputGroup);
    if (this.sessionLecturerAttendanceInputGroup) {
      this.subs.push(
        this.sessionLecturerAttendanceInputGroup.FG.valueChanges.subscribe((data) => {
          this.initFooterMenuItems(this.sessionLecturerAttendanceInputGroup);
        }),
      );
    }
  }

  onSendMessage() {

  }

  onParticipants() {
    if (!this.sessionData) return;

    this.modalService.openSessionAttendanceModal(SessionAttendanceModalComponent, this.sessionData.uuid);
  }

  onMaterials() {
    
  }

  onParticipantAttendance() {
    if (!this.sessionData) return;

    this.modalService.openSessionAttendanceModal(SessionAttendanceModalComponent, this.sessionData.uuid);
  }

  onCheckin() {
    if (!this.sessionData) return;

    this.navService.goToShopItemCheckin({ shopItemUuid: this.sessionData.shopItem.uuid, sessionUuid: this.sessionData.uuid, newTab: true });
  }

  private initInfoCards(org: IOrganization | null) {
    const block1: SessionDetailCardInfoCardType[] = [];
    const block2: SessionDetailCardInfoCardType[] = [];

    if (org) {
      if (org.uiSettings?.sessionDetailCardModalInfoCards1) {
        org.uiSettings.sessionDetailCardModalInfoCards1.forEach((c) => {
          block1.push(c);
        });
      } else {
        block1.push(SessionDetailCardInfoCardType.DATETIME, SessionDetailCardInfoCardType.TIME, SessionDetailCardInfoCardType.LOCATION);
      }

      if (org.uiSettings?.sessionDetailCardModalInfoCards2) {
        org.uiSettings.sessionDetailCardModalInfoCards2.forEach((c) => {
          block2.push(c);
        });
      } else {
        block2.push(SessionDetailCardInfoCardType.CHECKIN, SessionDetailCardInfoCardType.PARTICIPANTS, SessionDetailCardInfoCardType.PARTICIPANT_ATTENDANCE);
      }
    }

    this.infoCardBlock1 = block1;
    this.infoCardBlock2 = block2;
  }




  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.subs.forEach(sub => { sub.unsubscribe(); });
  }
}
